import React, { useMemo } from 'react';
import { BackgroundThemeRevision, ColorId } from 'referral-types';
import chroma from 'chroma-js';
import { rgbStringToHex } from '../../../../../utils/colorUtils';
import { useTheme } from '../../../../../context/theme/useTheme';
import { useBodyStyleVersion } from '../../../../../hooks/useBodyStyleVersion';
import { getCssVariableForColorId } from '../../../../../constants';

/* 
takes 3 inputs, but the 3rd is used for a "stroke value" that we are not using (stroke is set to 0): #000000, #008800, #ffffff

generated css:

background-color: #000000;
background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 6'%3E%3Cg stroke='%23FFF' stroke-width='0' %3E%3Ccircle fill='%23002400' cx='16' cy='32' r='32'/%3E%3Ccircle fill='%23002d00' cx='16' cy='28' r='28'/%3E%3Ccircle fill='%23003a00' cx='16' cy='24' r='24'/%3E%3Ccircle fill='%23004900' cx='16' cy='20' r='20'/%3E%3Ccircle fill='%23005800' cx='16' cy='16' r='16'/%3E%3Ccircle fill='%23006800' cx='16' cy='12' r='12'/%3E%3Ccircle fill='%23007800' cx='16' cy='8' r='8'/%3E%3Ccircle fill='%23008800' cx='16' cy='4' r='4'/%3E%3C/g%3E%3C/svg%3E");
background-attachment: fixed;
background-size: cover;

*/

const IntergalacticHighwayBackground: React.FC<{ themeRevision?: Partial<BackgroundThemeRevision> }> = (props) => {
  const { colorId1, colorId2 } = props.themeRevision || {};
  const { isDarkMode } = useTheme();
  const bodyStyleVersion = useBodyStyleVersion();

  const computedColors = useMemo(() => {
    const colorStyles1 = getCssVariableForColorId(colorId1 || ColorId.Primary2, isDarkMode);
    const colorStyles2 = getCssVariableForColorId(colorId2 || ColorId.Primary4, isDarkMode);
    const rootStyles = getComputedStyle(document.body);
    const c1 = rgbStringToHex(rootStyles.getPropertyValue(colorStyles1).trim() || '0 0 0');
    const c2 = rgbStringToHex(rootStyles.getPropertyValue(colorStyles2).trim() || '0 136 0');

    const colors = [chroma(c1), chroma(c2)];
    const scale = chroma.scale(colors).mode('hsl').colors(8);

    return scale;
  }, [colorId1, colorId2, isDarkMode, bodyStyleVersion]);

  const svgContent = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 6">
      <g stroke-width="0">
        <circle fill="${computedColors[0]}" cx="16" cy="32" r="32"/>
        <circle fill="${computedColors[1]}" cx="16" cy="28" r="28"/>
        <circle fill="${computedColors[2]}" cx="16" cy="24" r="24"/>
        <circle fill="${computedColors[3]}" cx="16" cy="20" r="20"/>
        <circle fill="${computedColors[4]}" cx="16" cy="16" r="16"/>
        <circle fill="${computedColors[5]}" cx="16" cy="12" r="12"/>
        <circle fill="${computedColors[6]}" cx="16" cy="8" r="8"/>
        <circle fill="${computedColors[7]}" cx="16" cy="4" r="4"/>
      </g>
    </svg>
  `;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        backgroundColor: computedColors[0],
        backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svgContent)}")`,
        backgroundAttachment: 'fixed',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        pointerEvents: 'none',
      }}
    />
  );
};

export default IntergalacticHighwayBackground;
